import { format } from 'date-fns';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { getOrganizationBadgeColor } from '@apps/web/lib/organization_display';
import { type AutoOutreachOrgDay } from '@libraries/typescript/api_client';

type Lane = 'auto_drafted' | 'agent_outreach' | 'manual_outreach';

const LANES: ReadonlyArray<{ readonly label: string; readonly value: Lane }> = [
    { label: 'Auto-drafted', value: 'auto_drafted' },
    { label: 'Agent outreach', value: 'agent_outreach' },
    { label: 'Manual outreach', value: 'manual_outreach' },
];

type TrendPoint = {
    readonly label: string;
    readonly [organizationId: string]: string | number;
};

type OrganizationSeries = {
    readonly id: string;
    readonly name: string;
    readonly color: string;
};

type TrendTooltipEntry = {
    readonly dataKey?: string | number;
    readonly name?: string;
    readonly value?: number;
    readonly color?: string;
};

function TrendTooltip({
    active,
    payload,
    label,
}: {
    readonly active?: boolean;
    readonly payload?: Array<TrendTooltipEntry>;
    readonly label?: string;
}) {
    const entries = (payload ?? []).filter((entry) => (entry.value ?? 0) > 0);
    if (!active || entries.length === 0) {
        return null;
    }
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">{label}</div>
            {entries.map((entry) => (
                <div
                    key={entry.dataKey}
                    className="flex items-center justify-between gap-4"
                >
                    <span style={{ color: entry.color }}>{entry.name}</span>
                    <span className="tabular-nums">{entry.value ?? 0}</span>
                </div>
            ))}
        </div>
    );
}

function countForLane(day: AutoOutreachOrgDay, lane: Lane): number {
    switch (lane) {
        case 'auto_drafted':
            return day.auto_drafted_count;
        case 'agent_outreach':
            return day.agent_outreach_count;
        case 'manual_outreach':
            return day.manual_outreach_count;
        default:
            return 0;
    }
}

// Legend and tooltip become unreadable with a hundred-plus org series, so
// only the busiest orgs for each lane get their own stack segment.
const MAX_ORGANIZATION_SERIES = 8;
const OTHER_SERIES_ID = '__other__';

function OrgTrendChartForLane({
    label,
    days,
    lane,
}: {
    readonly label: string;
    readonly days: Array<AutoOutreachOrgDay>;
    readonly lane: Lane;
}) {
    const totalsByOrg = new Map<string, number>();
    for (const day of days) {
        totalsByOrg.set(
            day.organization_id,
            (totalsByOrg.get(day.organization_id) ?? 0) +
                countForLane(day, lane),
        );
    }
    const topOrgIds = new Set(
        Array.from(totalsByOrg.entries())
            .filter(([, total]) => total > 0)
            .sort((left, right) => right[1] - left[1])
            .slice(0, MAX_ORGANIZATION_SERIES)
            .map(([id]) => id),
    );
    const activeOrgCount = Array.from(totalsByOrg.values()).filter(
        (total) => total > 0,
    ).length;
    const organizations: Array<OrganizationSeries> = Array.from(
        new Map(
            days
                .filter((day) => topOrgIds.has(day.organization_id))
                .map((day) => [
                    day.organization_id,
                    day.organization_name ?? day.organization_id,
                ]),
        ),
    )
        .sort((left, right) => left[1].localeCompare(right[1]))
        .map(([id, name]) => ({
            id,
            name,
            color: getOrganizationBadgeColor(id) ?? 'var(--chart-1)',
        }));
    if (activeOrgCount > topOrgIds.size) {
        organizations.push({
            id: OTHER_SERIES_ID,
            name: `Other (${activeOrgCount - topOrgIds.size} orgs)`,
            color: 'var(--muted-foreground)',
        });
    }
    const daysByDate = new Map<string, Array<AutoOutreachOrgDay>>();
    for (const day of days) {
        const entries = daysByDate.get(day.day) ?? [];
        entries.push(day);
        daysByDate.set(day.day, entries);
    }
    const data: Array<TrendPoint> = Array.from(daysByDate.entries())
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([day, entries]) => {
            let otherCount = 0;
            const orgCounts: Record<string, number> = {};
            for (const entry of entries) {
                const count = countForLane(entry, lane);
                if (topOrgIds.has(entry.organization_id)) {
                    orgCounts[entry.organization_id] = count;
                } else {
                    otherCount += count;
                }
            }
            return {
                label: format(new Date(`${day}T00:00:00`), 'M/d'),
                ...orgCounts,
                [OTHER_SERIES_ID]: otherCount,
            };
        });

    return (
        <div className="bg-background min-w-0 rounded-lg border p-2">
            <div className="text-muted-foreground mb-1 text-xs font-medium">
                {label}
            </div>
            {organizations.length === 0 ? (
                <div className="text-muted-foreground flex h-56 items-center justify-center rounded-md border text-sm">
                    No data to plot.
                </div>
            ) : (
                <div className="h-56 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={data}
                            margin={{ top: 4, right: 4, bottom: 0, left: -20 }}
                        >
                            <CartesianGrid
                                stroke="var(--border)"
                                strokeDasharray="3 3"
                            />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis
                                allowDecimals={false}
                                tick={{ fontSize: 11 }}
                            />
                            <RechartsTooltip content={<TrendTooltip />} />
                            <Legend wrapperStyle={{ fontSize: 10 }} />
                            {organizations.map((organization) => (
                                <Bar
                                    key={organization.id}
                                    dataKey={organization.id}
                                    name={organization.name}
                                    stackId="organizations"
                                    fill={organization.color}
                                    maxBarSize={32}
                                    isAnimationActive={false}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}

export function LaneOrgTrendChart({
    days,
}: {
    readonly days: Array<AutoOutreachOrgDay>;
}) {
    return (
        <div className="mt-2 flex flex-col gap-2 border-t pt-3">
            <div className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                Outreaches per day by organization
            </div>
            <div className="w-full max-w-full min-w-0 overflow-x-auto pb-2">
                <div className="grid w-full min-w-[66rem] grid-cols-3 gap-2">
                    {LANES.map((lane) => (
                        <OrgTrendChartForLane
                            key={lane.value}
                            label={lane.label}
                            days={days}
                            lane={lane.value}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
